import { Autoplay, Pagination, Navigation } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'

import { cn } from '@/lib/utils'

import { useBannerSliderVariants } from '../../../Theme'
import { NavigationButton } from '../shared'
import { BannerItem } from './BannerItem'
import { BannerSkeleton } from './BannerSkeleton'

import type { BannerSliderProps } from '../../types'

// Swiper styles are imported globally in styles/globals.css

/**
 * BannerSlider - Homepage banner carousel
 *
 * Features:
 * - Responsive images (desktop/mobile)
 * - Autoplay with pause on hover
 * - Touch/swipe support via Swiper
 * - Single banner mode (no slider) when only one banner
 * - Loading skeleton state
 * - CVA-based styling via UiProvider
 */
export function BannerSlider({
  banners,
  isLoading = false,
  autoplayDelay = 5000,
  autoplay = true,
  loop = true,
  className,
}: BannerSliderProps) {
  const {
    bannerSectionVariants,
    bannerSwiperVariants,
    swiperPaginationBullet,
    swiperPaginationBulletActive,
  } = useBannerSliderVariants()

  if (isLoading) {
    return <BannerSkeleton />
  }

  if (banners.length === 0) {
    return null
  }

  if (banners.length === 1) {
    return (
      <section className={cn(bannerSectionVariants(), className)}>
        <BannerItem banner={banners[0]} isFirst />
      </section>
    )
  }

  return (
    <section className={cn(bannerSectionVariants(), className)}>
      <Swiper
        modules={[Autoplay, Pagination, Navigation]}
        spaceBetween={0}
        slidesPerView={1}
        loop={loop}
        autoplay={
          autoplay
            ? {
                delay: autoplayDelay,
                disableOnInteraction: false,
                pauseOnMouseEnter: true,
              }
            : false
        }
        pagination={{
          clickable: true,
          bulletClass: swiperPaginationBullet,
          bulletActiveClass: swiperPaginationBulletActive,
        }}
        navigation={{
          nextEl: '.banner-swiper-next',
          prevEl: '.banner-swiper-prev',
        }}
        className={bannerSwiperVariants()}
      >
        {banners.map((banner, index) => (
          <SwiperSlide key={banner.id}>
            <BannerItem banner={banner} isFirst={index === 0} />
          </SwiperSlide>
        ))}

        <NavigationButton direction="prev" className="banner-swiper-prev" />
        <NavigationButton direction="next" className="banner-swiper-next" />
      </Swiper>
    </section>
  )
}
